import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SSE } from 'sse.js';
import { v4 } from 'uuid';
import { CHAT_BOT } from 'queries/query-keys';
import { MessageType } from 'ts-components/Chat';
import MachineLearningService from 'services/MachineLearningService';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

export type UseGenerationFlowEventStreamOptions = {
  chatSessionId: string;
  onGenerationStart?: () => void;
  onSuccess?: (data: any) => void;
  onFailure?: (data: any) => void;
  importResults?: boolean;
};

export const useGenerationFlowEventStream = ({
  chatSessionId,
  onGenerationStart,
  onSuccess,
  onFailure,
  importResults,
}: UseGenerationFlowEventStreamOptions) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const handleStartEvent = useCallback(
    (data: any) => {
      if (data?.flow_type) {
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (prev: any) => {
            const messages = [...prev.messages];

            // Remove trailing isResponseLoading messages
            while (
              messages.length > 0 &&
              messages[messages.length - 1]?.isResponseLoading
            ) {
              messages.pop();
            }

            // Only add if no isResponseRefining message exists
            const hasRefining = messages.some((msg) => msg.isResponseRefining);
            if (!hasRefining) {
              messages.push({
                id: v4(),
                messageContent: '',
                isUserMessage: false,
                isResponseLoading: false,
                isResponseRefining: true,
              });
            }

            return {
              ...prev,
              messages,
            };
          }
        );
      }
      onGenerationStart?.();
    },
    [queryClient, onGenerationStart, chatSessionId]
  );

  const handleRunningEvent = useCallback(
    (data: any) => {
      queryClient.setQueryData(
        CHAT_BOT.CHAT_SESSION(chatSessionId),
        (prev: { messages: MessageType[] } = { messages: [] }) => {
          const messages = [...prev.messages];
          const lastMessage = messages[messages.length - 1];
          const messageContent = data?.message;
          if (lastMessage?.isResponseRefining && !lastMessage.messageContent) {
            messages[messages.length - 1] = {
              ...lastMessage,
              id: 'running' + '-' + lastMessage.id,
              isResponseRefining: true,
              messageContent: messageContent,
              awaitingFinal: true,
            };
          } else if (
            lastMessage?.id.includes('running') &&
            data?.message === lastMessage?.messageContent
          ) {
            return prev;
          } else if (
            lastMessage?.id.includes('running') &&
            data?.message !== lastMessage?.messageContent
          ) {
            messages.splice(messages.length - 1, 1);
            messages.push({
              ...lastMessage,
              messageContent: data?.message,
              awaitingFinal: true,
            });
          }

          return {
            ...prev,
            messages,
          };
        }
      );
    },
    [queryClient, chatSessionId]
  );

  const removeRefiningMessage = useCallback(() => {
    queryClient.setQueryData(
      CHAT_BOT.CHAT_SESSION(chatSessionId),
      (prev: { messages: MessageType[] } = { messages: [] }) => {
        const messages = [...prev.messages];
        // Reverse scan to find and remove the most recent refining message
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i]?.isResponseRefining) {
            messages.splice(i, 1);
            break;
          }
        }
        return {
          ...prev,
          messages,
        };
      }
    );
  }, [queryClient, chatSessionId]);

  const handleSuccessEvent = useCallback(
    (data: any) => {
      queryClient.setQueryData(
        CHAT_BOT.CHAT_SESSION(chatSessionId),
        (prev: { messages: MessageType[] } = { messages: [] }) => {
          const messages = [...prev.messages];

          // remove loader
          messages.pop();
          // stop refresh action for generation var message
          messages[messages.length - 2].buildCompleted = true;
          messages.push({
            id: `complete-${v4()}`,
            messageContent: data?.message || t('Completed'),
            isUserMessage: false,
            isResponseLoading: false,
            isResponseRefining: false,
            showFeedbackIcons: false,
            role: 'assistant',
            awaitingFinal: false,
            buildCompleted: true,
          });
          return {
            ...prev,
            messages,
            last_flow: data,
            ready_for_action: false,
          };
        }
      );

      // Handle URL navigation if callback is provided and URL exists
      if (onSuccess && data) {
        onSuccess(data);
      }
    },
    [chatSessionId, queryClient, t, onSuccess]
  );

  const handleFailedEvent = useCallback(
    (data: any) => {
      removeRefiningMessage();
      queryClient.setQueryData(
        CHAT_BOT.CHAT_SESSION(chatSessionId),
        (prev: { messages: MessageType[] } = { messages: [] }) => {
          const newMessages = [...prev.messages];
          const lastMessage = newMessages[newMessages.length - 1];
          // stop refresh action for generation var message
          newMessages[newMessages.length - 2].buildCompleted = true;

          if (
            lastMessage?.isResponseLoading ||
            lastMessage?.isResponseRefining ||
            lastMessage?.id.includes('running')
          ) {
            newMessages.pop();
          }
          if (data?.message) {
            newMessages.push({
              id: `failed-${v4()}`,
              messageContent: data.message,
              isUserMessage: false,
              isResponseLoading: false,
              isResponseRefining: false,
              showFeedbackIcons: false,
              awaitingFinal: false,
              role: 'assistant',
            });
          }
          return {
            ...prev,
            messages: [...newMessages],
            ready_for_action: false,
          };
        }
      );
      onFailure?.(data);
    },
    [removeRefiningMessage, queryClient, chatSessionId, onFailure]
  );

  const startFlowStream = useCallback(
    async (
      sessionId: string,
      onStreamStart?: () => void,
      onStreamEnd?: () => void
    ) => {
      try {
        if (onStreamStart) onStreamStart();

        const eventSource = new SSE(
          MachineLearningService.getStartFlowStreamingEndpoint(sessionId),
          {
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
            withCredentials: true,
            payload: JSON.stringify({
              options: {
                import_result: importResults || false,
                in_sync: true,
              },
            }),
          }
        );

        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(sessionId),
          (prev: { messages: MessageType[] } = { messages: [] }) => {
            const messages = [...prev.messages];

            // Remove trailing isResponseLoading messages
            while (
              messages.length > 0 &&
              messages[messages.length - 1]?.isResponseLoading
            ) {
              messages.pop();
            }

            // Only add if no isResponseRefining message exists
            const hasRefining = messages.some((msg) => msg.isResponseRefining);
            if (!hasRefining) {
              messages.push({
                id: v4(),
                messageContent: '',
                isUserMessage: false,
                isResponseLoading: false,
                isResponseRefining: true,
              });
            }

            return {
              ...prev,
              messages,
            };
          }
        );

        const flowEventTypes = ['start', 'running', 'success', 'failed'];

        flowEventTypes.forEach((eventName) => {
          eventSource.addEventListener(eventName, (event: any) => {
            try {
              const data = event.data ? JSON.parse(event.data) : null;

              switch (eventName) {
                case 'start':
                  handleStartEvent(data);
                  break;
                case 'running':
                  handleRunningEvent(data);
                  break;
                case 'success':
                  handleSuccessEvent(data);
                  eventSource.close();
                  if (onStreamEnd) onStreamEnd();
                  break;
                case 'failed':
                  handleFailedEvent(data);
                  eventSource.close();
                  if (onStreamEnd) onStreamEnd();
                  break;
                default:
                  break;
              }
            } catch (error) {
              monitoringExceptionHelper(error);
            }
          });
        });

        eventSource.onerror = (error: any) => {
          eventSource.close();
          if (onStreamEnd) onStreamEnd();
          monitoringExceptionHelper(error);
        };
      } catch (error) {
        handleFailedEvent({
          error: t('Failed to start flow. Please try again.'),
        });
        if (onStreamEnd) onStreamEnd();
      }
    },
    [
      importResults,
      handleStartEvent,
      handleRunningEvent,
      handleSuccessEvent,
      handleFailedEvent,
      queryClient,
      t,
    ]
  );

  return {
    startFlowStream,
    handleStartEvent,
    handleRunningEvent,
    handleSuccessEvent,
    handleFailedEvent,
  };
};
